import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { GoogleMap, MapInfoWindow } from '@angular/google-maps';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { Doctor } from '@models/doctor.model';
import { Practice } from '@models/practice.model';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { AuthService } from '@services/auth.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { DisplayService } from '@services/display.service';
import { DoctorService } from '@services/doctor.service';
import { AutocompleteSuggestion, GoogleService } from '@services/google.service';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'gc-doctor-list',
  templateUrl: './doctor-list.component.html',
  styleUrls: ['./doctor-list.component.scss'],
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('0.5s ease-out', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('0.1s ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class DoctorListComponent implements OnInit, AfterViewInit, OnDestroy {
  // Variables
  doctors$ = new BehaviorSubject([]);
  filteredSpecialities$: Observable<string[]>;
  unsubscribe$ = new Subject<void>();
  locationPermissions = true;
  showMap = true;
  searchExpand = false;
  locationFocused = false;
  @Output() hamburgerClick = new EventEmitter<MouseEvent>();
  finishedLoading = false;
  markerOptions: google.maps.marker.AdvancedMarkerElementOptions;
  markerPositions: google.maps.LatLngLiteral[] = [];
  // Map Variables
  center: google.maps.LatLng;
  currentCoordinates: google.maps.LatLng;
  searchCoordinates: google.maps.LatLng;
  mapOptions: google.maps.MapOptions = {
    maxZoom: 18,
    zoom: 12,
    streetViewControl: true,
    mapTypeControl: false,
    zoomControl: false,
    fullscreenControl: false,
    mapId: 'doctorsSearchMap',
  };
  markers: google.maps.marker.AdvancedMarkerElement[] = [];
  myLocationMarker: google.maps.marker.AdvancedMarkerElement;

  @Input() practice: Practice;
  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) info: MapInfoWindow;
  @ViewChild('specialityAutocomplete') specialityAutocomplete: MatAutocomplete;
  isMobile$: BehaviorSubject<boolean>;
  specialities$: Observable<string[]>;
  searchForm: FormGroup;
  autocomplete: google.maps.places.Autocomplete;
  location: google.maps.places.PlaceResult;
  address$ = new BehaviorSubject('Unknown Address');
  geocoder: google.maps.Geocoder;
  address: string;
  filterForm: FormGroup;
  filteredDoctors$: Observable<Doctor[]>;
  specialityAutoCompleteControl: FormControl;
  currentAddress: string;
  doctorsLength = 0;
  infoWindowDoctors: Doctor[];
  addressSuggestions: AutocompleteSuggestion[] = [];
  private readonly googleService: GoogleService = inject(GoogleService);
  constructor(
    private readonly doctor: DoctorService,
    private readonly auth: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly alert: AlertService,
    private readonly display: DisplayService,
    private readonly configLocaleService: ConfigLocaleService,
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService,
  ) {
    this.isMobile$ = this.display.isMobile$;
    this.isMobile$.subscribe((isMobile) => {
      if (!isMobile) {
        this.showMap = true;
      }
    });

    this.searchForm = this.formBuilder.group(
      {
        location: [''],
        speciality: [''],
        searchTerm: [''],
      },
      { updateOn: 'submit' },
    );

    this.filterForm = this.formBuilder.group({
      orderControl: [''],
      inPersonControl: [false],
      telemedControl: [false],
    });
  }

  async populateAddressFields(event: Event) {
    this.addressSuggestions = await this.googleService.allAddressSugestions(
      (event.target as HTMLInputElement).value,
      this.configLocaleService.$addressAutoComplete(),
    );
  }

  async onOptionSelected(place: AutocompleteSuggestion) {
    this.address = place ? place.placePrediction?.text?.text : '';
    const placeGoogle = await this.googleService.getPlace(place);
    this.searchCoordinates = place ? new google.maps.LatLng(placeGoogle.location.lat, placeGoogle.location.lng) : undefined;
    this.searchForm.controls.location.setValue(this.address);
  }

  get isAuthed$() {
    return this.auth.authenticated$;
  }

  get onBookings() {
    return this.router.url.startsWith('/bookings/');
  }

  get searchResultText() {
    return [this.searchForm.value.searchTerm, this.searchForm.value.speciality].filter((v) => !!v).join(', ');
  }

  async ngOnInit() {
    await this.googleService.loadMapsLibrary();
    this.geocoder = new google.maps.Geocoder();

    this.specialityAutoCompleteControl = new FormControl('');

    this.filteredSpecialities$ = combineLatest([
      this.specialityAutoCompleteControl.valueChanges.pipe(startWith('')),
      this.doctor.getSpecialities$(),
    ]).pipe(
      debounceTime(200),
      map(([filterValue, specialities]) => specialities?.filter((s) => s?.toLowerCase().startsWith(filterValue?.toLowerCase()))),
    );

    this.route.queryParams.pipe(takeUntil(this.unsubscribe$))?.subscribe((params) => {
      const { latitude, longitude } = params;
      this.searchForm.controls.searchTerm.setValue(params.searchTerm);
      this.searchForm.controls.speciality.setValue(params.speciality);
      this.currentCoordinates = new google.maps.LatLng(parseFloat(latitude), parseFloat(longitude));
      this.searchCoordinates = new google.maps.LatLng(parseFloat(latitude), parseFloat(longitude));
    });

    // Filter Doctors on form or doctors change
    this.filteredDoctors$ = combineLatest([this.doctors$, this.filterForm.valueChanges]).pipe(
      debounceTime(500),
      map(([doctors, filterForm]) => {
        doctors = doctors.filter(
          (a) => (!filterForm.inPersonControl || a.hasInPersonBookings) && (!filterForm.telemedControl || a.hasTelemedBookings),
        );
        const doctorSorter = (a, b) => {
          let result = (a.distance ?? Infinity) - (b.distance ?? Infinity);
          if (filterForm.orderControl === 'time') {
            result = (a.earliestTimeslot?.unix() ?? Infinity) - (b.earliestTimeslot?.unix() ?? Infinity);
          }
          return result;
        };
        // Order doctors by order control
        const goodxSortedDoctors: Doctor[] = doctors.filter((x: Doctor) => !!x.uuid).sort(doctorSorter);
        const medibaseSortedDoctors: Doctor[] = doctors.filter((x: Doctor) => !x.uuid);
        const filteredDoctors = [].concat(goodxSortedDoctors, medibaseSortedDoctors.sort(doctorSorter));
        this.doctorsLength = filteredDoctors.length;
        this.doctor.updateTimeslots(filteredDoctors.filter((x) => x.uuid));
        this.refreshMap(filteredDoctors);
        this.finishedLoading = true;
        return filteredDoctors;
      }),
    );
    this.finishedLoading = false;
  }

  ngAfterViewInit() {
    this.searchForm.valueChanges.subscribe((form) => {
      // This does the search for when the search form changes
      this.getDoctorsFromBackend(form.searchTerm, form.speciality, this.searchCoordinates);
    });
    this.initializeMap().then(() => {
      this.fitMapToMarkers(this.currentCoordinates);
      this.filterForm.controls.orderControl.setValue('distance');
      const thePanorama = this.map.googleMap.getStreetView();
      google.maps.event.addListener(thePanorama, 'visible_changed', () => {
        // Display custom street view visible UI
        if (thePanorama.getVisible()) {
          const panorama = new google.maps.StreetViewPanorama(this.map.googleMap.getDiv(), {
            enableCloseButton: true,
            addressControlOptions: {
              position: google.maps.ControlPosition.TOP_CENTER,
            },
          });
          // rewrite default options
          this.map.googleMap.setStreetView(panorama);
        }
      });
    });
  }

  isLoadingTimeslots(doctor: Doctor): boolean {
    return this.doctor.isLoadingTimeslots(doctor);
  }

  refreshMap(doctors: Doctor[]) {
    this.removeAllMarkers(); // Correctly clear and add myLocationMarker.
    doctors.forEach((doc) => {
      if (doc.coordinates) {
        this.addMarker(doc.coordinates.latitude, doc.coordinates.longitude, doc);
      }
    });
    const coords = this.searchCoordinates ? this.searchCoordinates : this.currentCoordinates;
    this.fitMapToMarkers(coords);
  }

  createMarkerContent(iconUrl: string): HTMLElement {
    const img = document.createElement('img');
    img.src = iconUrl;
    return img;
  }

  async initializeMap() {
    await this.googleService.loadMapsLibrary();
    // Check if Coords set by Params
    if (!(this.route.snapshot.queryParams.latitude && this.route.snapshot.queryParams.longitude)) {
      // Get Coords by User Location
      await this.requestCoordinates();
    }
    this.currentAddress = await this.getLocationAddress();
    this.address = this.currentAddress;
    this.searchCoordinates = this.currentCoordinates;
    this.searchForm.controls.location.setValue(this.address);
    const { AdvancedMarkerElement: advancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    this.myLocationMarker = new advancedMarkerElement({
      position: this.currentCoordinates,
      title: this.translateService.instant('you_are_here'),
      content: this.createMarkerContent('assets/icons/mapmarker.png'),
      map: this.map.googleMap,
      gmpClickable: true,
    });
    this.markers.push(this.myLocationMarker);
    this.myLocationMarker.addEventListener('gmp-click', () => {
      const markerWrapper = new AdvancedMarkerElementWrapper(this.myLocationMarker);
      this.info.open(markerWrapper);
    });
  }

  async getLocationAddress() {
    return new Promise<string>((resolve) => {
      this.geocoder.geocode(
        { location: this.currentCoordinates },
        (
          results: {
            address_components: {
              short_name: string;
              long_name: string;
            }[];
          }[],
          status: string,
        ) => {
          if (status === 'OK') {
            let areaLocation = 0;
            let provinceLocation = 3;
            if (results[0].address_components.length > 6) {
              areaLocation = 3;
              provinceLocation = 5;
            }
            const areaLongName = results[0].address_components[areaLocation]?.short_name;
            const provinceName = results[0].address_components[provinceLocation]?.long_name;
            return resolve(areaLongName + ', ' + provinceName);
          } else {
            return resolve('Unknown Address');
          }
        },
      );
    });
  }

  ngOnDestroy() {
    this.doctor.stopTimeslotsGet();
    this.removeAllMarkers();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async getCoordinates(): Promise<Coordinates> {
    const position = await new Promise<Position>((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true }),
    );
    return position.coords;
  }

  async requestCoordinates() {
    try {
      const { latitude, longitude } = await this.getCoordinates();
      this.currentCoordinates = new google.maps.LatLng(latitude, longitude);
    } catch {
      this.locationPermissions = false;
      const latLong = this.configLocaleService.$locationJson();
      this.currentCoordinates = new google.maps.LatLng(latLong.latitude, latLong.longitude);
      this.alert.snackbar('please_allow_location_permissions');
    }
  }

  async addMarker(latitude: number, longitude: number, doctor: Doctor) {
    // https://mapmarker.io/documentation
    // This is used to clump markers
    let marker = this.markers.find(
      (x) =>
        x !== this.myLocationMarker &&
        Math.abs((x.position.lat as number) - latitude) <= 0.0001 &&
        Math.abs((x.position.lng as number) - longitude) <= 0.0001,
    );
    if (marker) {
      // Clump Together
      const markerDoctors: Doctor[] = marker['doctors'] || [];
      let iconUrl = 'https://cdn.mapmarker.io/api/v1/pin?size=50&text=' + markerDoctors.push(doctor);
      if (markerDoctors.find((x) => x.uuid)) {
        iconUrl += '&background=3c8dbc';
      } else if (markerDoctors.find((x) => x.isSaved)) {
        iconUrl += '&background=595ca9';
      }
      marker.content = this.createMarkerContent(iconUrl);
    } else {
      // Add new Marker
      const { AdvancedMarkerElement: advancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
      marker = new advancedMarkerElement({
        position: new google.maps.LatLng(latitude, longitude),
        title: doctor.diaryName,
        map: this.map.googleMap,
        content: this.createMarkerContent('assets/icons/mapmarker.png'),
        gmpClickable: true,
      });
      marker['doctors'] = [doctor];
      if (doctor.isSaved) {
        marker.content = this.createMarkerContent('assets/icons/saved_doctor_pin.png');
      }
      if (doctor.uuid) {
        const url = doctor.isSaved ? 'assets/icons/saved_gxweb_doctor_pin.png' : 'assets/icons/gxweb_doctor_pin.png';
        marker.content = this.createMarkerContent(url);
      }
      marker.addEventListener('gmp-click', () => {
        const markerWrapper = new AdvancedMarkerElementWrapper(marker);
        this.infoWindowDoctors = marker['doctors'];
        this.info.open(markerWrapper);
      });
    }
  }

  fitMapToMarkers(coords: google.maps.LatLng) {
    if (this.markers.length && this.locationPermissions) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(coords);
      this.markers.forEach((marker) => {
        bounds.extend(new google.maps.LatLng(marker.position.lat as number, marker.position.lng as number));
      });
      const padding = { top: 40, bottom: 20, left: 40, right: 20 };
      this.center = bounds.getCenter();
      this.map.fitBounds(bounds, padding);
    } else {
      this.center = coords;
    }
  }

  removeAllMarkers() {
    this.markers = [];
  }

  showBookingInfoDialog() {
    const html = `
      <div>
        <span>
          ${this.translateService.instant('in_person_consults_text')}
        </span>
        <br/>
        <br/>
        <span>
          ${this.translateService.instant('telemed_consults_text')}
        </span>
      </div>
    `;
    const config = { backdropClass: 'blur', hasBackdrop: true };
    this.alert.messageDialog$(this.translateService.instant('types_of_consults'), html, 'info', false, config);
  }

  clearFilters() {
    this.clearSpeciality();
    this.clearSearchTerm();
    this.clearLocation();
    this.filterForm.controls.inPersonControl.setValue(false);
    this.filterForm.controls.telemedControl.setValue(false);
  }

  getDoctorsFromBackend(searchTerm: string, speciality: string, coordinates: google.maps.LatLng) {
    // Update doctors on new coordinates or search term
    this.finishedLoading = false;

    this.doctor
      .getDoctors({
        lat: coordinates?.lat(),
        lng: coordinates?.lng(),
        searchTerm,
        speciality,
        practice: this.practice?.uuid,
      })
      .pipe(
        take(1),
        tap((docs) => this.doctors$.next(docs)),
      )
      .subscribe();
  }

  clearSpeciality() {
    this.specialityAutoCompleteControl.setValue('');
    this.searchForm.controls.speciality.setValue('');
  }

  clearSearchTerm() {
    this.searchForm.controls.searchTerm.setValue('');
  }

  clearLocation() {
    this.address = '';
    this.searchForm.controls.location.setValue('');
    this.searchCoordinates = this.currentCoordinates;
    this.getDoctorsFromBackend(this.searchForm.value.searchTerm, this.searchForm.value.speciality, this.searchCoordinates);
  }

  clickSearch() {
    this.searchExpand = false;
  }
}

class AdvancedMarkerElementWrapper {
  constructor(private readonly marker: google.maps.marker.AdvancedMarkerElement) {}

  getAnchor(): google.maps.MVCObject | google.maps.marker.AdvancedMarkerElement {
    return this.marker;
  }
}
